import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Paperclip, UploadSimple, X, FilePdf, FileImage, File } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable file attachment input.
 * Props:
 *  - value: file_id (string) or null
 *  - onChange(file_id|null, fileMeta?)
 */
export default function FileAttachment({ value, onChange, label = "Allegato", testid = "file" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState(null);

  // Fetch metadata when value changes (e.g., when editing)
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!value) { setMeta(null); return; }
      try {
        const { data } = await api.get(`/files/${value}`);
        if (!cancelled) setMeta(data);
      } catch {
        if (!cancelled) setMeta(null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [value]);

  async function upload(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File troppo grande (max 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMeta(data);
      onChange && onChange(data.id, data);
      toast.success("File caricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore upload");
    } finally {
      setUploading(false);
    }
  }

  function clear() {
    setMeta(null);
    onChange && onChange(null);
  }

  function IconFor({ ct }) {
    if (!ct) return <File size={18} />;
    if (ct.startsWith("image/")) return <FileImage size={18} />;
    if (ct === "application/pdf") return <FilePdf size={18} />;
    return <File size={18} />;
  }

  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {meta ? (
        <div className="flex items-center gap-3 border border-[#E2E8F0] rounded-sm p-2.5 bg-white">
          <IconFor ct={meta.content_type} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{meta.original_filename}</div>
            <div className="text-xs text-[#64748B]">{(meta.size / 1024).toFixed(1)} KB</div>
          </div>
          <a
            href={`${api.defaults.baseURL}/files/${meta.id}/download`}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline px-2"
            data-testid={`${testid}-view`}
          >
            Apri
          </a>
          <button type="button" onClick={clear} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`${testid}-remove`}>
            <X size={14} weight="bold" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload-btn`}
          className="w-full border border-dashed border-[#E2E8F0] rounded-sm px-3 py-3 text-sm text-[#64748B] hover:border-[#0033A0] hover:text-[#0033A0] transition-all flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>Caricamento…</>
          ) : (
            <><UploadSimple size={16} weight="bold" /> Carica file (PDF, JPG, PNG · max 10MB)</>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
